import { IoMdClose } from 'react-icons/io';
import { FiAlertTriangle } from 'react-icons/fi';
import PismoStatusBadge from './PismoStatusBadge';
import { formatCurrency, formatDate, formatCpfCnpj } from '../../../../utils/collateralFormatters';

const ERROR_STATUSES = ['REJECTED', 'FAILED_RETRYABLE', 'FAILED_PERMANENT'];

const Field = ({ label, value }: { label: string; value: React.ReactNode }) => {
    // Render 0 (e.g. attempts) as a real value; only null/undefined/'' fall back to —.
    const display = value === null || value === undefined || value === '' ? '—' : value;
    return (
        <div className="flex justify-between items-center py-1.5">
            <span className="text-xs text-gray-500 dark:text-gray-400">{label}</span>
            <span className="text-xs dark:text-white font-medium text-right max-w-[60%] break-words">{display}</span>
        </div>
    );
};

interface Props {
    item: any;
    onClose: () => void;
}

/**
 * Detail panel for one Pismo credit right registration: the record persisted on
 * our side plus the data Pismo returns on the enrichment GET (registryData: real
 * status, payment_status, org_id, timestamps) and the full interaction history
 * (operationLogs). The Pismo key is the internal_number — there is no separate
 * external id, so it is not shown.
 */
const CreditRightItemView = ({ item, onClose }: Props) => {
    if (!item) return null;

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative w-[440px] h-full bg-white dark:bg-slate-800 shadow-xl overflow-y-auto">
                <div className="sticky top-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between z-10">
                    <h3 className="text-sm font-semibold dark:text-white">Registro — {item.internalNumber || item.id}</h3>
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><IoMdClose size={18} /></button>
                </div>

                <div className="p-4 space-y-4">
                    {/* Registro */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Registro do Direito Creditório</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                            <div className="flex items-center justify-between py-1.5">
                                <span className="text-xs text-gray-500 dark:text-gray-400">Status</span>
                                <div className="flex items-center gap-1.5">
                                    {ERROR_STATUSES.includes(item.status) && (
                                        <FiAlertTriangle className="text-red-500" size={14} title={item.errorMessage || 'Erro no registro'} />
                                    )}
                                    <PismoStatusBadge status={item.status} statusFormatted={item.statusFormatted} />
                                </div>
                            </div>
                            <Field label="Registradora" value={item.registryName} />
                            <Field label="Nº interno" value={item.internalNumber ? <span className="font-mono text-[10px] break-all">{item.internalNumber}</span> : '—'} />
                            <Field label="Registrado em" value={item.registeredAt ? formatDate(item.registeredAt) : '—'} />
                            <Field label="Tentativas" value={item.attempts ?? 0} />
                            {item.errorCode && <Field label="Erro" value={<span className="text-red-600 dark:text-red-400">{item.errorCodeFormatted || item.errorMessage || item.errorCode}</span>} />}
                            {item.errorMessage && (
                                <div className="text-[11px] text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded p-2 mt-1">
                                    {item.errorMessage}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Dados na Pismo (retorno da registradora) */}
                    {item.registryData && (
                        <div>
                            <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Dados na Pismo</h4>
                            <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                                <Field label="Status na Pismo" value={item.registryData.pismoStatus || '—'} />
                                <Field label="Status de pagamento" value={item.registryData.paymentStatus ?? '—'} />
                                <Field label="Org ID" value={item.registryData.orgId ? <span className="font-mono text-[10px] break-all">{item.registryData.orgId}</span> : '—'} />
                                <Field label="Criado na Pismo" value={item.registryData.createdAt ? formatDate(item.registryData.createdAt) : '—'} />
                            </div>
                        </div>
                    )}

                    {/* Operação */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Operação</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                            <Field label="Operação" value={item.operationCode} />
                            <Field label="Contrato" value={item.contractNumber} />
                            <Field label="Cedente" value={item.assignorName} />
                            <Field label="CNPJ cedente" value={formatCpfCnpj(item.assignorTaxId)} />
                        </div>
                    </div>

                    {/* Título / Recebível */}
                    <div>
                        <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Título</h4>
                        <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3 space-y-0.5">
                            <Field label="NFe" value={item.nfe ? <span className="font-mono text-[10px] break-all">{item.nfe}</span> : '—'} />
                            <Field label="Nº título" value={item.documentTitle} />
                            <Field label="Parcela" value={item.installmentNumber} />
                            <Field label="Sacado" value={item.debtorName} />
                            <Field label="CPF/CNPJ sacado" value={formatCpfCnpj(item.debtorDocument)} />
                            <Field label="Valor de face" value={formatCurrency(item.faceValue)} />
                            <Field label="Emissão" value={item.issuingDate ? formatDate(item.issuingDate) : '—'} />
                            <Field label="Vencimento" value={item.maturityDate ? formatDate(item.maturityDate) : '—'} />
                            <Field label="Código IBGE" value={item.ibgeCode ? <span className="font-mono">{item.ibgeCode}</span> : '—'} />
                        </div>
                    </div>

                    {/* Histórico Pismo */}
                    {Array.isArray(item.operationLogs) && item.operationLogs.length > 0 && (
                        <div>
                            <h4 className="text-[10px] font-semibold uppercase text-gray-500 dark:text-gray-400 mb-2">Histórico (Pismo)</h4>
                            <div className="bg-gray-50 dark:bg-slate-900 rounded-lg p-3">
                                <ul className="space-y-1 max-h-60 overflow-y-auto">
                                    {item.operationLogs.map((log: any, i: number) => (
                                        <li key={i} className="text-[11px] flex items-start gap-2">
                                            <span className="text-gray-400 font-mono whitespace-nowrap">{formatDate(log.timestamp)}</span>
                                            <span className="dark:text-gray-300">
                                                <span className="font-medium">{log.action}</span>
                                                {log.data && Object.keys(log.data).length > 0 && (
                                                    <span className="text-gray-500 dark:text-gray-400"> — {JSON.stringify(log.data)}</span>
                                                )}
                                            </span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CreditRightItemView;
